import { Sites } from '@ps/platform-message-framework/common/Sites';
import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import { maxNumber, minNumber } from '@ps/web-shared-validators';
import { inRange } from 'lodash';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import type { Limits } from '../../../../types/responsibleGamingLimits';
import {
  validateAmountLimit,
  validateResponsibleGamingLimit
} from '../../../../validators/responsibleGamingLimits';
import AmountCurrency from '../../../AmountCurrency/AmountCurrency';
import ConversionSubLabel from '../../../ManageCurrencies/components/ConversionSubLabel/ConversionSubLabel';
import useFormatDisabledLimit from '../../hooks/useFormatDisabledLimit';

import {
  TXTWCL_Must_be_higher_than_or_equal_t_ELL,
  TXTWCL_Must_be_less_than_or_equal_to__ELL,
  TXTWCL_N0_Day_limit,
  TXTWCL_The_maximum_amount_for_this_li_ELL_1,
  TXTWCL_The_minimum_amount_for_this_li_ELL,
  TXTWCL_The_minimum_deposit_amount_for_ELL,
  TXTWCL_This_deposit_limit_may_be_too__ELL_1
} from './WeeklyLimit.messages.json';
import { warning } from './WeeklyLimit.scss';

interface Props {
  limits?: Limits;
  fieldName: string;
  currency: Currency;
  limitsDisabled?: boolean;
  showConversionInfo?: boolean;
  withPlaceholder?: boolean;
}

const WeeklyLimit: React.FC<Props> = ({
  fieldName,
  currency,
  limits,
  limitsDisabled,
  withPlaceholder = false,
  ...props
}) => {
  const swcRmfShowOnlyWeeklyLimit = useFeature(FeatureFlag.SwcRmfShowOnlyWeeklyLimit);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const showConversionInfo = useFeature(FeatureFlag.SwcDepositLimitCurrencyConversionInfo);
  const { limitMax, limitMin, limit } = limits?.weekly ?? {};
  const siteId = useSelector(select.cms.getSiteId);

  useField(`${fieldName}.weekly.previousLimit`, { initialValue: limit });
  const {
    input: { value: rate }
  } = useField(`${fieldName}.rate`, { subscription: { value: true } });
  const {
    input: { onChange: onDepositLimitsChange, value: weeklyLimit }
  } = useField(`${fieldName}.weekly.limit`, {
    initialValue: limit !== -1 ? limit : undefined,
    subscription: { value: true }
  });

  useFormatDisabledLimit(limitsDisabled, limit, onDepositLimitsChange);

  return (
    <>
      <AmountCurrency
        withPlaceholder={withPlaceholder}
        defaultCurrency={currency}
        disabled={limitsDisabled}
        includeCurrencies={[currency]}
        label={TXTWCL_N0_Day_limit}
        values={{ 0: 7 }}
        amountFieldName={`${fieldName}.weekly.limit`}
        currencyFieldName={`${fieldName}.currency`}
        onAmountBlur={() => {
          weeklyLimit && onDepositLimitsChange(Number(weeklyLimit).toFixed(2));
        }}
        extension={
          showConversionInfo && <ConversionSubLabel amount={weeklyLimit} fromCurrency={currency} />
        }
        validators={[
          validateAmountLimit(
            minNumber,
            TXTWCL_The_minimum_amount_for_this_li_ELL,
            limitMin,
            fieldName,
            rate
          ),
          validateAmountLimit(
            maxNumber,
            TXTWCL_The_maximum_amount_for_this_li_ELL_1,
            limitMax,
            fieldName,
            rate
          ),
          validateResponsibleGamingLimit(
            `${fieldName}.daily.limit`,
            limit,
            { ...TXTWCL_Must_be_higher_than_or_equal_t_ELL },
            minNumber
          ),
          validateResponsibleGamingLimit(
            `${fieldName}.monthly.limit`,
            limit,
            { ...TXTWCL_Must_be_less_than_or_equal_to__ELL, values: { 0: 30 } },
            maxNumber
          )
        ]}
        autofocus={swcRmfShowOnlyWeeklyLimit && isAutofocusEnabled}
        {...props}
      />
      {siteId === Sites.FRA && inRange(Number(weeklyLimit), 1, 10) && (
        <StarsInfo type="warning" className={warning}>
          <Translation {...TXTWCL_This_deposit_limit_may_be_too__ELL_1} />{' '}
          <Translation {...TXTWCL_The_minimum_deposit_amount_for_ELL} />
        </StarsInfo>
      )}
    </>
  );
};
export default WeeklyLimit;
